import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ArrowLeft, ExternalLink } from 'lucide-react'

import { MixLayout } from "@/components/layout"
import { ConnectionBanner } from '@/components/ui'
import { getCategoryImage } from '@/lib/categoryImages'
import { groupIngredientsByCategory, sortIngredientsByCategoryAndName } from '@/lib/ingredientCategories'
import { useWebSocketContext } from '@/contexts'

export default function RecipePage() {
  const { mixId = '', recipeId = '' } = useParams()
  const { isMix, loading, mixes, getRecipe } = useWebSocketContext()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !isMix(mixId)) {
      navigate('/home')
    }
  }, [loading, isMix, mixId, navigate])

  const mix = mixes.find(m => m.id === mixId)
  const recipe = mix ? getRecipe(mixId, recipeId) : undefined

  useEffect(() => {
    if (!loading && mix && !recipe) {
      navigate(`/mixes/${mixId}/recipes`)
    }
  }, [loading, mix, recipe, mixId, navigate])

  if (loading || !mix || !recipe) return null

  const recipeIngredients = mix.ingredients.filter(i =>
    recipe.ingredientIds.includes(i.id)
  )

  // Group and sort ingredients by category (same ordering as GroceryListPage)
  const groupedIngredients = groupIngredientsByCategory(recipeIngredients)
  const sortedGroupedIngredients = sortIngredientsByCategoryAndName(groupedIngredients)

  // Split instructions on \n\n first, then fallback to \n if there are no double-newlines
  const instructionBlocks = recipe.instructions.includes('\n\n')
    ? recipe.instructions.split('\n\n')
    : recipe.instructions.split('\n')

  const validInstructions = instructionBlocks
    .map(s => s.trim())
    .filter(s => s.length > 0)

  // Number steps only when blocks are short and look like imperative instructions
  const avgLength =
    validInstructions.reduce((sum, b) => sum + b.length, 0) /
    (validInstructions.length || 1)
  const shouldNumber =
    validInstructions.length > 1 && avgLength <= 150

  return (
    <MixLayout>
      <div className="sticky top-0 bg-background z-30">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(`/mixes/${mixId}/recipes`)}
            className="h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer flex-shrink-0"
            aria-label="Back to recipes"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-foreground truncate">Back to Recipes</h2>
          </div>
        </div>
        <ConnectionBanner />
      </div>

      <div className="w-full flex flex-col gap-6 px-4 pb-8 max-w-[768px] mx-auto">
        {/* Header: title+author left, image right on desktop */}
        <div className="flex flex-col md:flex-row md:items-start md:gap-8">
          {/* Image — first in DOM (mobile), last on desktop */}
          <div className="w-full rounded-xl overflow-hidden aspect-video md:order-last md:w-64 md:flex-none lg:w-80">
            {recipe.image ? (
              <img
                src={recipe.image}
                alt={recipe.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-muted">
                <span className="text-muted-foreground text-lg">🍳 Recipe</span>
              </div>
            )}
          </div>

          {/* Title and metadata */}
          <div className="flex-1 flex flex-col gap-2 pt-4 md:pt-0">
            <h1 className="text-2xl font-bold text-foreground leading-tight">
              {recipe.name}
            </h1>

            <div className="flex items-center gap-2 flex-wrap">
              {recipe.author && (
                <span className="inline-flex items-center text-sm text-muted-foreground">
                  By {recipe.author}
                </span>
              )}

              <a
                href={recipe.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 bg-muted rounded-full px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                {recipe.url ? new URL(recipe.url).hostname : ''}
              </a>
            </div>
          </div>
        </div>

        {/* Ingredients */}
        {recipeIngredients.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 min-w-0 overflow-hidden">
            <h2 className="text-lg font-semibold text-foreground">Ingredients</h2>
            <div className="flex flex-col gap-2 min-w-0">
              {(() => {
                const flat: typeof recipeIngredients = []
                for (const [, items] of Object.entries(sortedGroupedIngredients)) {
                  flat.push(...items)
                }
                return flat.map((ingredient) => (
                  <div
                    key={ingredient.id}
                    className="flex items-center gap-3 text-sm md:text-base"
                  >
                    {/* Category thumbnail */}
                    <div className="w-8 h-8 md:w-12 md:h-12 rounded-md bg-muted border border-border flex-shrink-0 overflow-hidden">
                      {(() => {
                        const catImg = getCategoryImage(ingredient.category)
                        return catImg ? (
                          <img
                            src={catImg}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-background">
                            <span className="text-lg md:text-2xl">🍽️</span>
                          </div>
                        )
                      })()}
                    </div>

                    {/* Name */}
                    <span className="flex-1 text-foreground min-w-0 truncate">
                      {ingredient.name}
                    </span>

                    {/* Quantity + Unit */}
                    <span className="whitespace-nowrap flex-shrink-0">
                      {ingredient.quantity ? (
                        <>
                          <span className="text-foreground">{ingredient.quantity}</span>
                          {ingredient.unit && (
                            <span className="text-muted-foreground"> {ingredient.unit}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </span>
                  </div>
                ))
              })()}
            </div>
          </div>
        )}

        {/* Instructions */}
        {validInstructions.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3">
            <h2 className="text-lg font-semibold text-foreground">Instructions</h2>
            <div className="flex flex-col gap-4">
              {validInstructions.map((block, index) => (
                <div key={index} className="flex gap-3 text-sm text-foreground leading-relaxed">
                  {shouldNumber && (
                    <span className="flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                      {index + 1}
                    </span>
                  )}
                  <p className={shouldNumber ? 'mt-0.5' : ''}>{block}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MixLayout>
  )
}
